/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridItem from '../../../components/Grid/GridItem';
import { useDispatch, useSelector } from 'react-redux';
import { SimCardServiceTypeCollection, SimCardServiceTypeItem } from '../../../interfaces/collections/SimCardServiceInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import i18n from '../../../core/i18n';
import ActivateFeatureButton from '../../../components/Buttons/ActivateFeatureButton';
import { SimpleSelect, SimpleSelectElements } from '../../../components/Adescom/SimpleSelect';
import { ProductActions, ProductComponent } from '../../../modules/simcard/ProductReducer';
import { ComponentParamsDialog } from './ComponentParamsDialog';
import { notificationConstants } from '../../../modules/notifications/notifications';
import { FeatureVerbTypeMap } from '../../../modules/features/FeaturesTypes';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import SelectApiList from '../../lib/SelectApiList';

const _ = require('underscore');

type ComponentByGroupType = {
    simCardId: number;
    groupId: number;
    groupName: string;
    simCard?: SimCardInterface;
};

export interface ComponentParam {
    componentName: string;
    simCardId: number;
    status: number;
    params: string;
    extraPackage?: string;
    extraAmount?: string;
}

export const ComponentByGroup: FunctionComponent<ComponentByGroupType> = ({ simCardId, groupId, groupName, simCard }): ReactElement => {
    const dispatch = useDispatch();
    const [groupFeaturesList, setGroupFeaturesList] = useState<Array<SimCardServiceTypeItem> | null>(null);
    const [selectedComponent, setSelectedComponent] = useState<number | null>(null);
    const { selectedValue, type } = useSelector((state: { productReducers: ProductComponent }) => state.productReducers);
    const [openDialog, setOpenDialog] = useState(false);
    const [definitionComponent, setDefinitionComponent] = useState<SimCardServiceTypeItem | null>(null);
    const [verbType, setVerbType] = useState(FeatureVerbTypeMap.ACTIVATE);

    useEffect(() => {
        if (type === ProductActions.PRODUCT_ACTIVATE_COMPONENT_SELECTED && groupFeaturesList && groupFeaturesList.length > 0) {
            for (let i = 0; i < groupFeaturesList.length; i++) {
                if (groupFeaturesList[i].id === selectedValue) {
                    setSelectedComponent(selectedValue);
                    break;
                }
            }
        }
    }, [groupFeaturesList, type, selectedValue]);

    useEffect(() => {
        if (groupFeaturesList === null) {
            const loadItems = new ItemLoaders(dispatch);
            loadItems.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types')),
                'group_load_features_list',
                [
                    { name: 'simCardId', value: simCardId },
                    { name: 'pagination', value: false },
                    { name: 'groupId', value: groupId },
                ],
                [{ name: 'description', value: 'asc' }],
                (data: SimCardServiceTypeCollection): void => {
                    setGroupFeaturesList(data['hydra:member']);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, groupFeaturesList, groupId, simCardId]);

    const confirmCallback = (): void => {
        if (selectedComponent && groupFeaturesList) {
            for (let i = 0; i < groupFeaturesList.length; i++) {
                if (groupFeaturesList[i].id === selectedComponent) {
                    setDefinitionComponent(groupFeaturesList[i]);
                    setOpenDialog(true);
                    break;
                }
            }
        }
    };

    const activateCallback = (componentParams: ComponentParam): void => {
        const itemLoader = new ItemLoaders(dispatch);
        if (componentParams.extraAmount && componentParams.extraPackage) {
            const extraComponent: ComponentParam = _.clone(componentParams);
            extraComponent.componentName = componentParams.extraPackage;
            const params: any = JSON.parse(componentParams.params);
            for (let i = 0; i < params.length; i++) {
                if (params[i].name === 'amount') {
                    params[i].value = componentParams.extraAmount;
                }
            }
            extraComponent.params = JSON.stringify(params);
            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/operation_on_services')),
                'post_active_component_with_params',
                extraComponent,
                (data: any): void => {
                    extraComponent.componentName = '';
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/operation_on_services')),
            'post_active_component_with_params',
            componentParams,
            (data: any): void => {
                setOpenDialog(false);
                dispatch({
                    type: notificationConstants.SUCCESS,
                    message: 'simcard:f.activate_component_request_send',
                });
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };
    const closeCallback = (): void => setOpenDialog(false);

    const elementList: Array<SimpleSelectElements> = [];
    if (groupFeaturesList) {
        groupFeaturesList.map((item) => {
            if (item.simCardServiceTypeParam && item.simCardServiceTypeParam.length > 0) {
                for (let i = 0; i < item.simCardServiceTypeParam.length; i++) {
                    if (item.simCardServiceTypeParam[i].verb === verbType && (item.allowedOperation === 1 || item.allowedOperation === 3)) {
                        elementList.push({ value: item.id, name: item.description + ' (' + item.mainname + ')' ?? 'no-name' });
                    }
                }
            } else if (item.allowedOperation === 1 || item.allowedOperation === 3) {
                elementList.push({ value: item.id, name: item.description + ' (' + item.mainname + ')' ?? 'no-name' });
            }
        });
    }

    const callbackResponseParser = (groupFeaturesList:any): Array<any> => {
        const elementListLocal: Array<SimpleSelectElements> = [];
        if (groupFeaturesList['hydra:member']) {
            groupFeaturesList['hydra:member'].map((item:any) => {
                if (item.simCardServiceTypeParam && item.simCardServiceTypeParam.length > 0) {
                    for (let i = 0; i < item.simCardServiceTypeParam.length; i++) {
                        if (item.simCardServiceTypeParam[i].verb === FeatureVerbTypeMap.ACTIVATE && (item.allowedOperation === 1 || item.allowedOperation === 3)) {
                            elementListLocal.push({ value: item.id, name: item.description + ' (' + item.mainname + ')' ?? 'no-name' });
                        }
                    }
                } else if (item.allowedOperation === 1 || item.allowedOperation === 3 ) {
                    elementListLocal.push({ value: item.id, name: item.description + ' (' + item.mainname + ')' ?? 'no-name' });
                }
            })
        }
        return elementListLocal

    }

    return (
        <React.Fragment>
            {groupFeaturesList && groupFeaturesList.length > 0 && (
                <React.Fragment>
                    <GridItem xs={10}>

                        <SelectApiList
                            callbackResponseParser={callbackResponseParser}
                            optionValue={elementList}
                            addEmpty={true}
                            emptyLabel={groupName}
                            filters={[
                                { name: 'simCardId', value: simCardId },
                                { name: 'groupId', value: groupId },
                                { name: 'itemsPerPage', value: 20 }
                            ]}
                            orders={[{ name: 'description', value: 'asc' }]}
                            iri={FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types'))}
                            fieldName={['searchTerm']}
                            handleChange={(fieldName, value): void => {
                                setSelectedComponent(value[0])
                            }}
                            useType={1}
                        />

                    </GridItem>
                    {UserRoleHelper.checkRole(roles.ROLE_BLOCK_OPERATION_ON_SERVICES_POST) &&
                        !UserRoleHelper.checkRole(roles.ROLE_SUBSCRIPTION_VIEW_ONLY) && (
                            <GridItem xs={2} style={{ textAlign: 'right', marginTop: '15px' }}>
                                <ActivateFeatureButton
                                    callback={confirmCallback}
                                    callbackParam={{}}
                                    nonTableButton={true}
                                    tooltipTitle={i18n.t('simcard:f.activate_feature')}
                                    size="sm"
                                />
                            </GridItem>
                        )}
                </React.Fragment>
            )}
            {definitionComponent && (
                <ComponentParamsDialog
                    verbType={verbType}
                    open={openDialog}
                    definitionComponent={definitionComponent}
                    closeCallback={closeCallback}
                    confirmCallback={activateCallback}
                    isActivationMode={true}
                    simCardId={simCardId}
                    simCard={simCard}
                    typeList={groupFeaturesList}
                />
            )}
        </React.Fragment>
    );
};
